import { type TimeFrame } from "@features/data-export/types";
import { type User } from "@internal/user";
import {
  type DateInterval,
  type FixedDateInterval,
  parseDateInterval as nbCommonParseDateRange,
  type DateRangeChoiceV2,
  type ICompareDateRangeEnum,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularity,
  type ITimeGranularityV2,
  MCAttributionWindowDays,
  NBAccountingMode,
  dateRangeToInterval,
  parseDateRangeQueryParam,
  resolveCompareDateRange,
  TimeGranularity_Deprecated,
  isFixedDateInterval,
  CompareDateRangeEnum,
} from "@north-beam/nb-common";

export interface ReportState {
  readonly dateRange: DateRangeChoiceV2 | FixedDateInterval;
  readonly compareDateRange: ICompareDateRangeEnum | FixedDateInterval;
  readonly attributionModel: string;
  readonly nbAccountingMode: INBAccountingMode;
  readonly attributionWindow: IMCAttributionWindowDays;
  readonly timeGranularity: ITimeGranularity;
}

export function parseCompareDateRange(
  value: string | null,
): ICompareDateRangeEnum | FixedDateInterval | null {
  if (value === null) {
    return null;
  }
  try {
    const rv = JSON.parse(decodeURIComponent(value));
    if (typeof rv === "string") {
      return CompareDateRangeEnum.fromString(rv);
    }

    if (typeof rv === "object") {
      if (!isFixedDateInterval(rv)) {
        return null;
      }
      return rv as FixedDateInterval;
    }

    return null;
  } catch (e) {
    return null;
  }
}

export const parseDateRange = (value: string | null) => {
  // This is an outdated date range value.
  if (value === "yesterdayAndToday") {
    return value;
  }
  // Restrict what is formerly referred to as DateRangeChoiceV2
  return parseDateRangeQueryParam(value, {
    restrictTo: [
      "today",
      "yesterday",
      "last3Days",
      "last7Days",
      "last14Days",
      "last28Days",
      "last60Days",
      "lastMonth",
      "monthToDate",
      "yearToDate",
    ] as const,
  });
};

export function parseState(user: User, query: string): ReportState {
  const params = new URLSearchParams(query);

  const attributionModel = user.getDefaultAttributionModel(
    params.get("attributionModel"),
  );
  const dateRange = parseDateRange(params.get("dateRange")) ?? "last28Days";
  const compareDateRange =
    parseCompareDateRange(params.get("compareDateRange")) ?? "lastPeriod";
  const attributionWindow =
    MCAttributionWindowDays.fromString(params.get("attributionWindow")) ?? "1";
  const nbAccountingMode =
    NBAccountingMode.fromString(params.get("nbAccountingMode")) ?? "accrual";
  const timeGranularity =
    TimeGranularity_Deprecated.fromString(params.get("timeGranularity")) ??
    "daily";

  return {
    attributionModel,
    dateRange,
    compareDateRange,
    nbAccountingMode,
    attributionWindow,
    timeGranularity,
  };
}

export function makeReportStateQuery(state: ReportState): URLSearchParams {
  const search = new URLSearchParams();
  search.set("attributionModel", state.attributionModel);
  search.set("dateRange", JSON.stringify(state.dateRange));
  search.set("compareDateRange", JSON.stringify(state.compareDateRange));
  search.set("nbAccountingMode", state.nbAccountingMode);
  search.set("attributionWindow", state.attributionWindow);
  search.set("timeGranularity", state.timeGranularity);
  return search;
}

export interface ReportGQLVariables {
  dateRange: DateInterval;
  attributionMethod: any;
  nbAccountingMode: "accrual" | "cash";
  attributionWindows: string[];
  compareDateRange: DateInterval;
  timeGranularity: string;
}

export function reportStateToGQLVariables(
  state: Omit<ReportState, "dateRange" | "timeGranularity"> & {
    dateRange: TimeFrame;
    timeGranularity: ITimeGranularityV2;
  },
  anchor: string,
): ReportGQLVariables {
  const asRange =
    typeof state.dateRange === "string"
      ? dateRangeToInterval(state.dateRange)
      : state.dateRange;
  const dateRange = nbCommonParseDateRange(asRange, anchor);
  const compareDateRange = resolveCompareDateRange(
    dateRange,
    state.compareDateRange,
  );
  const attributionMethod = state.attributionModel ?? "northbeam_custom";
  const timeGranularity = state.timeGranularity ?? "daily";

  const attributionWindows = [state.attributionWindow ?? "1"];
  if (timeGranularity !== "hourly") {
    attributionWindows.push("infinity");
  }

  return {
    dateRange,
    attributionMethod,
    nbAccountingMode: state.nbAccountingMode,
    attributionWindows,
    compareDateRange,
    timeGranularity,
  };
}
